import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { undo, redo } from 'prosemirror-history';
import { useActor } from '@xstate/react';
import { dryEditorClass } from '.';
import FastTooltip from '../../components/FastTooltip';
import { GlobalStateContext } from '../../state/app/index';
import { getNotePermalink } from '../../utils/permalinkUtils';

function copyToClipboard(text, onSuccess) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(() => fallbackCopy(text, onSuccess));
  } else {
    fallbackCopy(text, onSuccess);
  }
}

function fallbackCopy(text, onSuccess) {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  try {
    document.execCommand('copy');
    onSuccess?.();
  } finally {
    document.body.removeChild(el);
  }
}

const NoteOverflowMenu = ({ viewMode, onSetViewMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const globalServices = useContext(GlobalStateContext);
  const [appState, sendAppState] = useActor(globalServices.appState);
  const selectedNoteId = appState?.context?.selectedNoteId;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right });
    }
    setIsOpen((prev) => !prev);
  };

  const handleUndo = () => {
    undo(dryEditorClass.getEditorState(), dryEditorClass.getDispatch());
    setIsOpen(false);
  };

  const handleRedo = () => {
    redo(dryEditorClass.getEditorState(), dryEditorClass.getDispatch());
    setIsOpen(false);
  };

  const handleViewMode = (mode) => {
    onSetViewMode(viewMode === mode ? 'editor' : mode);
    setIsOpen(false);
  };

  const handleCopy = () => {
    const text = dryEditorClass?.editorView ? dryEditorClass.getEditorStateText() : '';
    copyToClipboard(text, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    setIsOpen(false);
  };

  const handleShowHistory = () => {
    sendAppState({ type: 'SelectTab', tabName: 'history' });
    setIsOpen(false);
  };

  const handleCopyDeeplink = () => {
    if (!selectedNoteId) return;
    const url = getNotePermalink(selectedNoteId);
    copyToClipboard(url, () => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
    setIsOpen(false);
  };

  const menu = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="note-overflow-menu__dropdown"
      style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
    >
      <button className="note-overflow-menu__item" onClick={handleUndo} type="button">
        <UndoIcon className="note-overflow-menu__icon" />
        <span>Undo</span>
        <span className="note-overflow-menu__hint">Ctrl+Z</span>
      </button>
      <button className="note-overflow-menu__item" onClick={handleRedo} type="button">
        <RedoIcon className="note-overflow-menu__icon" />
        <span>Redo</span>
        <span className="note-overflow-menu__hint">Ctrl+Y</span>
      </button>

      <div className="note-overflow-menu__divider" />

      <button
        className={`note-overflow-menu__item note-overflow-menu__item--md${viewMode === 'markdown' ? ' active' : ''}`}
        onClick={() => handleViewMode('markdown')}
        type="button"
      >
        <span className="note-overflow-menu__label">MD</span>
        <span>View as Markdown</span>
      </button>
      <button
        className={`note-overflow-menu__item note-overflow-menu__item--html${viewMode === 'html' ? ' active' : ''}`}
        onClick={() => handleViewMode('html')}
        type="button"
      >
        <span className="note-overflow-menu__label">HTML</span>
        <span>View as HTML</span>
      </button>
      <button
        className={`note-overflow-menu__item note-overflow-menu__item--slack${viewMode === 'slack' ? ' active' : ''}`}
        onClick={() => handleViewMode('slack')}
        type="button"
      >
        <span className="note-overflow-menu__label">Slack</span>
        <span>Copy for Slack</span>
      </button>

      <div className="note-overflow-menu__divider" />

      <button className="note-overflow-menu__item" onClick={handleShowHistory} type="button" disabled={!selectedNoteId}>
        <i className="material-icons note-overflow-menu__icon">history</i>
        <span>Note history</span>
      </button>

      <div className="note-overflow-menu__divider" />

      <button className="note-overflow-menu__item" onClick={handleCopy} type="button">
        <i className="material-icons note-overflow-menu__icon">{copied ? 'check' : 'content_copy'}</i>
        <span>{copied ? 'Copied!' : 'Copy note text'}</span>
      </button>
      <button className="note-overflow-menu__item" onClick={handleCopyDeeplink} type="button" disabled={!selectedNoteId}>
        <i className="material-icons note-overflow-menu__icon">{copiedLink ? 'check' : 'link'}</i>
        <span>{copiedLink ? 'Copied!' : 'Copy deeplink'}</span>
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div className="note-overflow-menu">
      <FastTooltip title="More options">
        <button
          ref={btnRef}
          className={`editor-button${isOpen ? ' active' : ''}`}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
          title="More options"
          type="button"
        >
          <span>
            <MoreVertIcon sx={{ color: '#ffffff', fontSize: '1.5rem' }} />
          </span>
        </button>
      </FastTooltip>
      {menu}
    </div>
  );
};

export default NoteOverflowMenu;
